/** Public build metadata contains no credentials, paths, or ride data. */
export interface RenderRelease { readonly format: 1; readonly fingerprint: string }
export const RENDER_RELEASE_HEADER = 'X-Overland-Render-Release';
export const RENDER_RELEASE_PATH = '/api/render/release';

export function isRenderRelease(value: unknown): value is RenderRelease {
  if (!value || typeof value !== 'object') return false;
  const fields = value as Record<string, unknown>;
  return fields.format === 1 && typeof fields.fingerprint === 'string'
    && /^[a-f0-9]{64}$/.test(fields.fingerprint) && Object.keys(fields).length === 2;
}

export function sameRenderRelease(actual: unknown, expected: RenderRelease): boolean {
  return isRenderRelease(actual) && actual.format === expected.format && actual.fingerprint === expected.fingerprint;
}

/** Read only the bounded metadata document, including on service bindings. */
export async function readRenderRelease(response: Response): Promise<RenderRelease | null> {
  if (!response.ok || !response.headers.get('Content-Type')?.includes('application/json') || !response.body) return null;
  const reader = response.body.getReader();
  const chunks: Uint8Array[] = [];
  let length = 0;
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      length += value.length;
      if (length > 1024) { await reader.cancel(); return null; }
      chunks.push(value);
    }
    const bytes = new Uint8Array(length);
    let offset = 0;
    for (const chunk of chunks) { bytes.set(chunk, offset); offset += chunk.length; }
    const value: unknown = JSON.parse(new TextDecoder().decode(bytes));
    return isRenderRelease(value) ? value : null;
  } catch { return null; }
  finally { reader.releaseLock(); }
}

export function releaseHeader(release: RenderRelease): string { return `${release.format}:${release.fingerprint}`; }

export function releaseRequestMatches(request: Request, expected: RenderRelease): boolean {
  return request.headers.get(RENDER_RELEASE_HEADER) === releaseHeader(expected);
}

/** Completed jobs and callbacks remain available during a release. */
export function isRenderSubmission(request: Request): boolean {
  return request.method === 'POST' && /^\/api\/(?:local\/renders|2026-09\/render-jobs(?:\/[a-f0-9-]+\/retry)?)$/.test(new URL(request.url).pathname);
}

export function releaseMismatchResponse(): Response {
  return Response.json({ error: { code: 'render_release_mismatch',
    message: 'The studio and video service are being updated. Reload the studio and try again.' } },
  { status: 409, headers: { 'Cache-Control': 'no-store' } });
}
